// getLiveStreamData.js
'use strict';
import { Collection } from 'immutable';
import createCachedSelector from '../resource/createCachedSelector.js';
import getOperationData from '../selector/getOperationData.js';
import { GroupPrivateStatus } from '../resource/liveStreamConstants.js';

const defaultSelectPath = [];
const defaultDataKey = '';

/**
 * Select live stream data by live stream
 * @kind selector
 * @param {Immutable.Map} state - root state.
 * @param {array} [selectPath = []] - select path. default is [].
 * @param {string} [dataKey = ''] - data key. default is ''.
 * @return {any} The selected live stream data.
 */
const getLiveStreamData = createCachedSelector(
  (state, selectPath = defaultSelectPath) =>
    state.getIn(['liveStream', ...selectPath]),
  (state, selectPath, dataKey) => dataKey,
  (liveStream, dataKey = defaultDataKey) => {
    let result = liveStream;
    if (dataKey) {
      result = liveStream?.get(dataKey);
    }
    return result instanceof Collection ? result.toJS() : result;
  }
)(
  (state, selectPath = defaultSelectPath, dataKey = defaultDataKey) =>
    `${selectPath.join(':')}:${dataKey}`
);

/**
 * get groups private trigger is accepted by stream id.
 * @kind selector
 * @param {Immutable.Map} state - root state.
 * @param {string} streamId - stream id.
 * @return {boolean}  groups private trigger is accepted or not.
 */
export const getIsGroupPrivateAccepted = createCachedSelector(
  (state, streamId) =>
    getLiveStreamData(state, [streamId, 'groupPrivate'], 'status'),
  (state, streamId) =>
    getOperationData(
      state,
      ['liveStream', 'paid', streamId],
      'isPrivateStreamUnlocked'
    ),
  (groupPrivateStatus, isPrivateStreamUnlocked) => {
    return (
      groupPrivateStatus === GroupPrivateStatus.ACCEPTED &&
      isPrivateStreamUnlocked
    );
  }
)((state, streamId) => streamId);

/**
 * check if group private is triggered by other users.
 * @kind selector
 * @param {Immutable.Map} state - root state.
 * @param {string} streamId - stream id.
 * @return {boolean} true/false group private is triggered by other users.
 */
export const getIsGroupPrivateTriggeredByOthers = createCachedSelector(
  (state, streamId) =>
    getLiveStreamData(state, [streamId, 'groupPrivate'], 'status'),
  (state, streamId) =>
    getOperationData(
      state,
      ['liveStream', 'paid', streamId],
      'isPrivateStreamUnlocked'
    ),
  (groupPrivateStatus, isPrivateStreamUnlocked) => {
    return (
      groupPrivateStatus === GroupPrivateStatus.CONSIDERING &&
      !isPrivateStreamUnlocked
    );
  }
)((state, streamId) => streamId);

/**
 * check if group private is triggered by myself.
 * @kind selector
 * @param {Immutable.Map} state - root state.
 * @param {string} streamId - stream id.
 * @return {boolean} true/false group private is triggered by myself.
 */
export const getIsGroupPrivateTriggeredByMyself = createCachedSelector(
  (state, streamId) =>
    getLiveStreamData(state, [streamId, 'groupPrivate'], 'status'),
  (state, streamId) =>
    getOperationData(
      state,
      ['liveStream', 'paid', streamId],
      'isPrivateStreamUnlocked'
    ),
  (groupPrivateStatus, isPrivateStreamUnlocked) => {
    return (
      groupPrivateStatus === GroupPrivateStatus.CONSIDERING &&
      isPrivateStreamUnlocked
    );
  }
)((state, streamId) => streamId);

export default getLiveStreamData;
